import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect, useCallback } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Edit2, Trash2, FileText, Calendar, CheckCircle2, Clock, Download } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapWorkTrackingPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: new Date().toISOString().split('T')[0], status: 'yapildi' });
  const [saving, setSaving] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfDateFrom, setPdfDateFrom] = useState('');
  const [pdfDateTo, setPdfDateTo] = useState('');
  const [pdfPeriod, setPdfPeriod] = useState('haftalik');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('work-view-mode') || 'day'); // day | period
  const [period, setPeriod] = useState(() => localStorage.getItem('work-period') || '7d');
  const [rangeStart, setRangeStart] = useState(() => localStorage.getItem('work-range-start') || '');
  const [rangeEnd, setRangeEnd] = useState(() => localStorage.getItem('work-range-end') || '');

  useEffect(() => { localStorage.setItem('work-view-mode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('work-period', period); }, [period]);
  useEffect(() => { localStorage.setItem('work-range-start', rangeStart); }, [rangeStart]);
  useEffect(() => { localStorage.setItem('work-range-end', rangeEnd); }, [rangeEnd]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (viewMode === 'period') {
        if (period === 'custom' && rangeStart && rangeEnd) {
          url = `${API}/work-tasks?date_from=${rangeStart}&date_to=${rangeEnd}`;
        } else if (period !== 'custom') {
          url = `${API}/work-tasks?period=${period}`;
        } else {
          setLoading(false); setTasks([]); return;
        }
      } else {
        url = `${API}/work-tasks?date=${filterDate}`;
      }
      const res = await fetch(url, { headers });
      if (res.ok) setTasks(await res.json());
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, viewMode, period, rangeStart, rangeEnd]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const saveTask = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlık gerekli'); return; }
    setSaving(true);
    try {
      const url = editingTask ? `${API}/work-tasks/${editingTask.id}` : `${API}/work-tasks`;
      const method = editingTask ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      toast.success(editingTask ? 'Güncellendi' : 'Eklendi');
      setShowDialog(false); setEditingTask(null);
      setForm({ title: '', description: '', date: filterDate, status: 'yapildi' });
      loadTasks();
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/work-tasks/${id}`, { method: 'DELETE', headers });
      toast.success('Silindi');
      loadTasks();
    } catch { toast.error('Silme başarısız'); }
  };

  const openEdit = (t) => {
    setEditingTask(t);
    setForm({ title: t.title, description: t.description || '', date: t.date, status: t.status || 'yapildi' });
    setShowDialog(true);
  };

  const generatePdf = async () => {
    if (!pdfDateFrom || !pdfDateTo) { toast.error('Tarih aralığı seçin'); return; }
    setGeneratingPdf(true);
    try {
      const res = await fetch(`${API}/work-tasks/pdf?date_from=${pdfDateFrom}&date_to=${pdfDateTo}`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `is-raporu_${pdfDateFrom}_${pdfDateTo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
      setShowPdfDialog(false);
    } catch { toast.error('PDF oluşturulamadı'); }
    finally { setGeneratingPdf(false); }
  };

  const setQuickPeriod = (period) => {
    const today = new Date();
    let from, to;
    if (period === 'haftalik') {
      const dayOfWeek = today.getDay();
      from = new Date(today);
      from.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      to = new Date(from);
      to.setDate(from.getDate() + 6);
    } else if (period === 'aylik') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }
    setPdfDateFrom(from.toISOString().split('T')[0]);
    setPdfDateTo(to.toISOString().split('T')[0]);
    setPdfPeriod(period);
  };

  const navigateDate = (dir) => {
    const d = new Date(filterDate);
    d.setDate(d.getDate() + dir);
    setFilterDate(d.toISOString().split('T')[0]);
  };

  const dayLabel = new Date(filterDate + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4" data-testid="work-tracking-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">İş Takibi</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowPdfDialog(true); setQuickPeriod('haftalik'); }} data-testid="pdf-report-btn">
            <FileText className="h-4 w-4 mr-1" />PDF Rapor
          </Button>
          <Button onClick={() => { setEditingTask(null); setForm({ title: '', description: '', date: filterDate, status: 'yapildi' }); setShowDialog(true); }}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-1" />İş Ekle
          </Button>
        </div>
      </div>

      {/* Görünüm Seçici */}
      <div className="flex items-center gap-2 bg-white border rounded-lg p-1 w-fit">
        <button onClick={() => setViewMode('day')}
          className={`px-3 py-1.5 text-xs font-semibold rounded transition ${viewMode === 'day' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid="view-mode-day">Günlük</button>
        <button onClick={() => setViewMode('period')}
          className={`px-3 py-1.5 text-xs font-semibold rounded transition ${viewMode === 'period' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid="view-mode-period">Dönem Özeti</button>
      </div>

      {viewMode === 'day' ? (
        <>
          {/* Tarih Seçimi */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>&larr;</Button>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="w-44" />
            </div>
            <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>&rarr;</Button>
            <span className="text-sm text-slate-500">{dayLabel}</span>
            <Button variant="ghost" size="sm" onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}>Bugün</Button>
          </div>
        </>
      ) : (
        <>
          {/* Dönem Seçici */}
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: '24h', label: 'Bugün' },
              { id: '7d', label: '1 Hafta' },
              { id: '30d', label: '1 Ay' },
              { id: '90d', label: '3 Ay' },
              { id: '365d', label: '1 Yıl' },
              { id: 'custom', label: 'Tarih Aralığı' },
            ].map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${period === p.id ? 'bg-emerald-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-emerald-50'}`}
                data-testid={`work-period-${p.id}`}>{p.label}</button>
            ))}
            {period === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)}
                  className="border rounded-md px-2 py-1 text-xs h-8" data-testid="work-range-start" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}
                  className="border rounded-md px-2 py-1 text-xs h-8" data-testid="work-range-end" />
              </div>
            )}
          </div>

          {/* Dönem Özet Kartları */}
          {!loading && (() => {
            const total = tasks.length;
            const done = tasks.filter(t => t.status === 'yapildi').length;
            const pending = tasks.filter(t => t.status === 'bekliyor' || t.status === 'askida').length;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-3">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Toplam İş</p>
                  <p className="text-2xl font-black text-emerald-900 mt-1">{total}</p>
                </div>
                <div className="bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 rounded-xl p-3">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-sky-700">Yapıldı</p>
                  <p className="text-2xl font-black text-sky-900 mt-1">{done}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-3">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-amber-700">Bekliyor</p>
                  <p className="text-2xl font-black text-amber-900 mt-1">{pending}</p>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Arama */}
      <input type="text" value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder="Başlık veya açıklamada ara..."
        className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:border-emerald-500"
        data-testid="work-search-input" />

      {/* İş Listesi */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : (() => {
        const q = searchQuery.trim().toLowerCase();
        const visible = q ? tasks.filter(t =>
          (t.title || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
        ) : tasks;
        if (visible.length === 0) {
          return (
            <Card><CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">{q ? 'Sonuç bulunamadı' : 'Bu güne ait kayıt yok'}</p>
              {!q && (
                <Button variant="outline" className="mt-3" onClick={() => { setEditingTask(null); setForm({ title: '', description: '', date: filterDate, status: 'yapildi' }); setShowDialog(true); }}>
                  <Plus className="h-4 w-4 mr-1" />İş Ekle
                </Button>
              )}
            </CardContent></Card>
          );
        }
        return (
          <div className="space-y-2">
            {visible.map((t, i) => (
            <Card key={t.id} className="group">
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`mt-0.5 ${t.status === 'yapildi' ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {t.status === 'yapildi' ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{t.title}</p>
                  {t.description && <p className="text-sm text-slate-500 mt-0.5 whitespace-pre-wrap">{t.description}</p>}
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    {t.date ? new Date(t.date + 'T00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
                    {t.created_at && (
                      <>
                        <span className="text-slate-300">·</span>
                        {new Date(t.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}>
                    <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTask(t.id)}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            ))}
          <p className="text-sm text-slate-500 text-center pt-2">Toplam {visible.length} iş kaydı{q && ` · ${tasks.length} kayıt arasından`}</p>
        </div>
        );
      })()}

      {/* İş Ekle/Düzenle Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) setEditingTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingTask ? 'İş Düzenle' : 'Yeni İş Kaydı'}</DialogTitle></DialogHeader>
          <form onSubmit={saveTask} className="space-y-3">
            <div className="space-y-1">
              <Label>Tarih</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Yapılan iş..." autoFocus data-testid="task-title-input" />
            </div>
            <div className="space-y-1">
              <Label>Açıklama</Label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full border rounded-lg p-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Detaylı açıklama..." />
            </div>
            <div className="space-y-1">
              <Label>Durum</Label>
              <div className="flex gap-2">
                <Button type="button" variant={form.status === 'yapildi' ? 'default' : 'outline'} size="sm" className={form.status === 'yapildi' ? 'bg-emerald-600' : ''}
                  onClick={() => setForm({ ...form, status: 'yapildi' })}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Yapıldı
                </Button>
                <Button type="button" variant={form.status === 'devam' ? 'default' : 'outline'} size="sm" className={form.status === 'devam' ? 'bg-amber-600' : ''}
                  onClick={() => setForm({ ...form, status: 'devam' })}>
                  <Clock className="h-3.5 w-3.5 mr-1" />Devam Ediyor
                </Button>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); setEditingTask(null); }} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PDF Rapor Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>İş Raporu PDF</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant={pdfPeriod === 'haftalik' ? 'default' : 'outline'} size="sm" onClick={() => setQuickPeriod('haftalik')} className="flex-1">Haftalık</Button>
              <Button variant={pdfPeriod === 'aylik' ? 'default' : 'outline'} size="sm" onClick={() => setQuickPeriod('aylik')} className="flex-1">Aylık</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Başlangıç</Label>
                <Input type="date" value={pdfDateFrom} onChange={e => { setPdfDateFrom(e.target.value); setPdfPeriod(''); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bitiş</Label>
                <Input type="date" value={pdfDateTo} onChange={e => { setPdfDateTo(e.target.value); setPdfPeriod(''); }} />
              </div>
            </div>
            <Button onClick={generatePdf} disabled={generatingPdf} className="w-full bg-blue-600 hover:bg-blue-700">
              {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              PDF İndir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
